<script setup lang="ts">
/**
 * 提示条容器。
 *
 * 直接渲染 toast-store 里的列表，不用 provide/inject：状态本来就只有一份，
 * 再包一层只会让"到底谁在管这些提示"更难回答。`markToastProviderMounted`
 * 的唯一作用是让 `useToast()` 知道"现在有地方能显示提示"。
 */

import { onBeforeUnmount, onMounted } from 'vue';

import { cn } from '../../lib/cn';
import { markToastProviderMounted, removeToast, toastState, type ToastTone } from './toast-store';

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'border-brand-200 bg-white text-slate-800',
  success: 'border-brand-200 bg-brand-50 text-brand-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  danger: 'border-red-200 bg-red-50 text-red-900',
};

let unmountProvider: (() => void) | null = null;

onMounted(() => {
  unmountProvider = markToastProviderMounted();
});

onBeforeUnmount(() => {
  unmountProvider?.();
  unmountProvider = null;
});
</script>

<template>
  <slot />
  <div
    class="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
    role="status"
    aria-live="polite"
  >
    <div
      v-for="item in toastState.items"
      :key="item.id"
      :class="cn('pointer-events-auto w-full max-w-sm rounded-lg border px-4 py-3 shadow-lg animate-fade-in', TONE_CLASS[item.tone ?? 'info'])"
    >
      <div class="flex items-start gap-3">
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium">{{ item.title }}</p>
          <p v-if="item.description !== undefined" class="mt-1 text-xs leading-relaxed opacity-90">
            {{ item.description }}
          </p>
        </div>
        <button
          type="button"
          aria-label="关闭提示"
          class="shrink-0 rounded p-1 text-xs opacity-60 hover:opacity-100"
          @click="removeToast(item.id)"
        >
          ✕
        </button>
      </div>
    </div>
  </div>
</template>
